import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';

import { apiErrorFromResponse } from '@/shared/api/errors';
import { api, buildUrl } from '@/shared/api/http';
import { getValidAccessToken, refreshAccessToken } from '@/shared/auth/token';
import type { CursorPage, FileRef, PagedResult, UserRef } from '@/shared/api/types';
import type { ReactionSummary, ReactionType } from '@/shared/components/reactions/reactions';

/* ------------------------------------------------------------------ wire types (docs/modules/tasks.md) */

export type TaskStatus = 'pending' | 'inProgress' | 'awaitingReview' | 'completed' | 'deferred' | 'declined';
export type TaskPriority = 'normal' | 'high';
export type TaskAction =
  'start' | 'pause' | 'complete' | 'approve' | 'returnToWork' | 'defer' | 'resume' | 'renew' | 'decline';
export type TaskRole = 'creator' | 'responsible' | 'accomplice' | 'auditor' | 'manager';

export interface TaskGroup {
  id: string;
  name: string;
  avatarUrl: string | null;
  isProject: boolean;
}

export interface TaskRef {
  id: string;
  number: number;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  deadline: string | null;
  startPlan: string | null;
  endPlan: string | null;
  responsible: UserRef | null;
  isOverdue: boolean;
}

export interface TaskListItem {
  id: string;
  number: number;
  title: string;
  status: TaskStatus;
  priority: TaskPriority;
  deadline: string | null;
  startPlan: string | null;
  endPlan: string | null;
  createdAt: string;
  activityAt: string;
  closedAt: string | null;
  creator: UserRef | null;
  responsible: UserRef | null;
  accomplices: UserRef[];
  group: TaskGroup | null;
  parentId: string | null;
  tags: string[];
  checklist: { done: number; total: number };
  subtaskCount: number;
  commentCount: number;
  newCommentCount: number;
  attachmentCount: number;
  timeSpentSeconds: number;
  estimateSeconds: number | null;
  isOverdue: boolean;
  isTimerRunning: boolean;
  myRoles: TaskRole[];
  allowedActions: TaskAction[];
  canEdit: boolean;
  canChangeDeadline: boolean;
  canChangePlan: boolean;
  boardRank: number;
}

export interface TaskPermissions {
  edit: boolean;
  delete: boolean;
  changeDeadline: boolean;
  changePlan: boolean;
  delegate: boolean;
  manageMembers: boolean;
  manageChecklist: boolean;
  attach: boolean;
  trackTime: boolean;
  comment: boolean;
  follow: boolean;
  /** The reminder before the deadline: those who do the work and those who direct it. */
  changeReminder?: boolean;
}

export interface ChecklistItem {
  id: string;
  text: string;
  isDone: boolean;
  assignee: UserRef | null;
  sortOrder: number;
  doneAt: string | null;
}

export interface Checklist {
  id: string;
  title: string;
  sortOrder: number;
  items: ChecklistItem[];
}

export interface TaskAttachment {
  file: FileRef;
  addedBy: UserRef | null;
  addedAt: string;
  canRemove: boolean;
}

export interface TaskTimer {
  taskId: string;
  number: number;
  title: string;
  startedAt: string;
}

export interface TaskDetail {
  id: string;
  number: number;
  title: string;
  descriptionHtml: string;
  status: TaskStatus;
  priority: TaskPriority;
  deadline: string | null;
  startPlan: string | null;
  endPlan: string | null;
  createdAt: string;
  updatedAt: string;
  activityAt: string;
  statusChangedAt: string;
  closedAt: string | null;
  closedBy: UserRef | null;
  creator: UserRef | null;
  responsible: UserRef | null;
  accomplices: UserRef[];
  auditors: UserRef[];
  group: TaskGroup | null;
  parent: TaskRef | null;
  tags: string[];
  estimateSeconds: number | null;
  timeSpentSeconds: number;
  requireResult: boolean;
  requiresReview: boolean;
  allowChangeDeadline: boolean;
  hasResult: boolean;
  isOverdue: boolean;
  checklists: Checklist[];
  subtasks: TaskRef[];
  predecessors: TaskRef[];
  successors: TaskRef[];
  attachments: TaskAttachment[];
  commentCount: number;
  newCommentCount: number;
  myRoles: TaskRole[];
  isFollowing: boolean;
  allowedActions: TaskAction[];
  permissions: TaskPermissions;
  myTimer: TaskTimer | null;
  /** Records of other modules linked to the task (a goal): label `t('<module>:taskLinks.<type>')`, link `url`. */
  externalLinks: TaskExternalLink[];
  /** The recurring task that made this one, and the ones that repeat it. */
  recurrence?: TaskRecurrenceInfo | null;
  /** Minutes before the deadline the task reminds (0, 15, 60, 240, 1440, 2880); `null` — the portal's default; 0 — never. */
  reminderMinutes?: number | null;
  /** The lead time the task reminds with: its own, else the portal's default. */
  effectiveReminderMinutes?: number;
}

export interface TaskExternalLink {
  module: string;
  type: string;
  /** Null when hidden. */
  id: string | null;
  title: string;
  url: string;
  /** The reader may not open the record (a goal of a map they do not see): only that the task is linked shows. */
  hidden?: boolean;
}

export interface TaskCounters {
  overdue: number;
  newComments: number;
  total: number;
  doing: number;
  assisting: number;
  created: number;
  following: number;
  awaitingMyReview: number;
  /** Open tasks of the people the user manages (department heads). */
  managed: number;
  managedOverdue: number;
  /** How many people the user manages: 0 hides the «Руковожу» tab. */
  managedPeople: number;
}

export interface TaskProject {
  id: string;
  name: string;
  avatarUrl: string | null;
  isProject: boolean;
  openTasks: number;
  totalTasks: number;
}

export interface TaskBoard {
  items: TaskListItem[];
  truncated: boolean;
  /** How many tasks match when the board is cut (the board holds 500); otherwise the number shown. */
  total?: number;
}

export interface Dependency {
  predecessorId: string;
  successorId: string;
}

export interface GanttData {
  group: TaskGroup;
  tasks: TaskListItem[];
  dependencies: Dependency[];
  canCreate: boolean;
  /** The group has more tasks than the chart holds (the first 500 by start); `total` says how many. */
  truncated?: boolean;
  total?: number;
}

/** The caller's access to a group's task views (`GET /api/tasks/groups/{id}/access`). */
/** May the caller set up the robots and processes of a project's tasks (its owner, moderators, administrators; not while archived). */
export interface TaskGroupAutomation {
  canManage: boolean;
}

export interface TaskGroupAccess {
  /** May add tasks to the group: members, administrators in open groups — nobody while the group is archived. */
  canCreate: boolean;
  /** May subscribe to the group's realtime topic `task-group:{id}`. */
  canFollow: boolean;
}

export type HistoryKind =
  | 'created'
  | 'title'
  | 'description'
  | 'status'
  | 'priority'
  | 'deadline'
  | 'plan'
  | 'responsible'
  | 'accompliceAdded'
  | 'accompliceRemoved'
  | 'auditorAdded'
  | 'auditorRemoved'
  | 'group'
  | 'parent'
  | 'tags'
  | 'estimate'
  | 'flags'
  | 'checklistItemAdded'
  | 'checklistItemDone'
  | 'checklistItemUndone'
  | 'checklistItemRemoved'
  | 'attachmentAdded'
  | 'attachmentRemoved'
  | 'dependencyAdded'
  | 'dependencyRemoved'
  | 'timeSpent'
  | 'resultAdded'
  | 'groupDeleted'
  | 'recurring'
  | 'creator'
  | 'trashed'
  | 'restored'
  | 'reminder';

/** What made a change when it was not a person (docs/modules/bizproc.md §3.7): a robot of a stage, a process, a trigger or the system. */
export type AutomationKind = 'robot' | 'process' | 'trigger' | 'system';

export interface HistoryEntry {
  id: string;
  kind: HistoryKind;
  actor: UserRef | null;
  /** Set when a robot, process or trigger made the change; `actorName` is the name of its definition. */
  actorKind?: AutomationKind | null;
  actorName?: string | null;
  oldValue: string | null;
  newValue: string | null;
  oldUser: UserRef | null;
  newUser: UserRef | null;
  createdAt: string;
}

export interface TaskComment {
  id: string;
  author: UserRef;
  /** Set when a robot, process or trigger wrote the comment; `authorName` is the name of its definition. */
  authorKind?: AutomationKind | null;
  authorName?: string | null;
  bodyHtml: string;
  attachments: FileRef[];
  createdAt: string;
  editedAt: string | null;
  reactions: ReactionSummary[];
  canEdit: boolean;
  canDelete: boolean;
  isResult: boolean;
}

export interface TimeEntry {
  id: string;
  user: UserRef | null;
  startedAt: string;
  stoppedAt: string | null;
  seconds: number;
  comment: string | null;
  isManual: boolean;
  isRunning: boolean;
  canEdit: boolean;
}

export interface TaskTime {
  entries: TimeEntry[];
  totals: { user: UserRef | null; seconds: number }[];
  totalSeconds: number;
  estimateSeconds: number | null;
  canTrack: boolean;
}

export interface PlanStage {
  id: string;
  title: string;
  color: string | null;
  sortOrder: number;
}

export interface PlanEntry {
  task: TaskListItem;
  stageId: string | null;
  rank: number;
}

export interface Plan {
  stages: PlanStage[];
  entries: PlanEntry[];
  truncated: boolean;
  /** How many open tasks the user has when the plan is cut (it holds 500). */
  total?: number;
}

/** List / board filters (query string of `GET /api/tasks`). */
export interface TaskFilters {
  role?: 'all' | 'responsible' | 'accomplice' | 'creator' | 'auditor' | 'participant' | 'worker' | 'managed';
  userId?: string;
  status?: string;
  search?: string;
  groupId?: string;
  responsibleId?: string;
  creatorId?: string;
  priority?: TaskPriority;
  deadline?: 'overdue' | 'none' | 'set';
  deadlineFrom?: string;
  deadlineTo?: string;
  tag?: string;
  parentId?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
  /** Tasks this person is an accomplice (co-executor) of, whatever the preset. */
  accompliceId?: string;
  /** Tasks this person follows (an auditor). */
  auditorId?: string;
  /** Tasks whose responsible person has left (is deactivated). */
  noResponsible?: boolean;
  createdFrom?: string;
  createdTo?: string;
  closedFrom?: string;
  closedTo?: string;
}

export interface CreateTaskInput {
  title: string;
  descriptionHtml?: string;
  responsibleId?: string;
  accompliceIds?: string[];
  auditorIds?: string[];
  priority?: TaskPriority;
  deadline?: string | null;
  startPlan?: string | null;
  endPlan?: string | null;
  groupId?: string | null;
  parentId?: string | null;
  tags?: string[];
  estimateSeconds?: number | null;
  requireResult?: boolean;
  requiresReview?: boolean;
  allowChangeDeadline?: boolean;
  attachmentIds?: string[];
  checklists?: { title: string; items: string[] }[];
  /** The form was filled from this template: its subtasks are made with the task. */
  templateId?: string | null;
  /** Minutes before the deadline the task reminds; absent — the portal's default. */
  reminderMinutes?: number | null;
}

/** PATCH body: only the properties present change; `null` clears. */
export type UpdateTaskInput = Partial<{
  title: string;
  descriptionHtml: string;
  priority: TaskPriority;
  deadline: string | null;
  startPlan: string | null;
  endPlan: string | null;
  responsibleId: string;
  accompliceIds: string[];
  auditorIds: string[];
  groupId: string | null;
  parentId: string | null;
  tags: string[];
  estimateSeconds: number | null;
  requireResult: boolean;
  requiresReview: boolean;
  allowChangeDeadline: boolean;
  reminderMinutes: number | null;
}>;

/* ------------------------------------------------------------------ manager view, templates, recurring tasks */

/** One person of the «Руковожу» overview (`GET /api/tasks/team`). */
export interface TaskTeamMember {
  user: UserRef;
  open: number;
  overdue: number;
  awaitingReview: number;
}

export interface TaskTeam {
  /** Administrators have the manager view on. */
  enabled: boolean;
  managedCount: number;
  truncated: boolean;
  members: TaskTeamMember[];
}

/** Who may export task lists (the administrator's switch). */
export type ExportPolicy = 'everyone' | 'admins' | 'nobody';

/** The administrator switches of the module (`GET/PUT /api/tasks/settings`). */
export interface TaskSettings {
  managerViewEnabled: boolean;
  canManage: boolean;
  exportPolicy?: ExportPolicy;
  /** The caller may export (the policy lets them). */
  canExport?: boolean;
  /** The reminder before a deadline of a task that has none of its own (minutes; a setting of the installation). */
  defaultReminderMinutes?: number;
  /** How long deleted tasks stay in the trash (0: until somebody deletes them for good). */
  trashRetentionDays?: number;
}

/** `PUT /api/tasks/settings`: only what is present changes. */
export interface UpdateTaskSettingsInput {
  managerViewEnabled?: boolean;
  exportPolicy?: ExportPolicy;
}

/* ------------------------------------------------------------------ trash, bulk actions, saved filters */

/** An entry of the task trash (`GET /api/tasks/trash`). */
export interface TrashItem {
  id: string;
  number: number;
  title: string;
  status: TaskStatus;
  group: TaskGroup | null;
  creator: UserRef | null;
  responsible: UserRef | null;
  deletedBy: UserRef | null;
  deletedAt: string;
  /** When the cleanup erases it for good (null: the cleanup is off). */
  purgeAt: string | null;
  subtasks: number;
  comments: number;
  timeSpentSeconds: number;
  canRestore: boolean;
  canPurge: boolean;
}

export interface TrashPage {
  items: TrashItem[];
  page: number;
  pageSize: number;
  total: number;
  retentionDays: number;
  /** More entries than the 500 newest the list reads. */
  truncated: boolean;
}

export interface TrashQuery {
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface RestoreResult {
  id: string;
  number: number;
  title: string;
  /** The responsible person had left: the one who restored the task is the responsible person now. */
  reassigned: boolean;
  /** The project was deleted meanwhile: the task is back without one. */
  projectLost: boolean;
}

export interface PurgeResult {
  purged: number;
}

export type BulkAction =
  'deadline' | 'responsible' | 'project' | 'priority' | 'tags' | 'watchers' | 'status' | 'delete';

/** `POST /api/tasks/bulk`: the selected ids (≤ 500) or the list's own filter (the first 500), and the action's parameters. */
export interface BulkRequest {
  action: BulkAction;
  ids?: string[];
  filter?: TaskFilters;
  deadline?: string | null;
  responsibleId?: string;
  groupId?: string | null;
  priority?: TaskPriority;
  mode?: 'add' | 'remove' | 'set';
  tags?: string[];
  userIds?: string[];
  statusAction?: TaskAction;
}

export type BulkSkipCode =
  | 'not_found'
  | 'forbidden'
  | 'read_only'
  | 'invalid_transition'
  | 'result_required'
  | 'unchanged'
  | 'invalid'
  | 'conflict';

/** A task a bulk action left as it was; a task the caller may not see has no number and title. */
export interface BulkSkipped {
  id: string;
  number: number | null;
  title: string | null;
  code: BulkSkipCode;
}

export interface BulkResult {
  requested: number;
  updated: number;
  skipped: BulkSkipped[];
  /** «Everything by the filter» matched more than the server takes (500). */
  truncated: boolean;
  total: number;
}

export type SavedFilterScope = 'personal' | 'shared';

/** A saved filter: the list state as the parameters of the page's address. */
export interface SavedFilter {
  id: string;
  name: string;
  scope: SavedFilterScope;
  owner: UserRef | null;
  canEdit: boolean;
  query: Record<string, string>;
  updatedAt: string;
}

export interface SaveFilterInput {
  name: string;
  scope: SavedFilterScope;
  query: Record<string, string>;
}

/** A file the export produced, and what the server says about it. */
export interface ExportedFile {
  blob: Blob;
  fileName: string;
  /** The list had more tasks than the file holds. */
  truncated: boolean;
  rows: number;
}

export type DeadlineUnit = 'minutes' | 'hours' | 'days';

/** "The deadline is N units after the task is made." */
export interface DeadlineOffset {
  value: number;
  unit: DeadlineUnit;
}

export interface TemplateResponsible {
  /** `me`: whoever makes the task; `user`: `userId`. */
  mode: 'me' | 'user';
  userId?: string | null;
}

export interface TemplateChecklist {
  title: string;
  items: string[];
}

export interface TemplateSubtask {
  title: string;
  responsibleId?: string | null;
  deadline?: DeadlineOffset | null;
}

/** What a template (and a recurring task) makes. */
export interface TemplateBody {
  title: string;
  descriptionHtml: string;
  priority: TaskPriority;
  responsible: TemplateResponsible;
  accompliceIds: string[];
  auditorIds: string[];
  groupId?: string | null;
  tags: string[];
  estimateSeconds?: number | null;
  requireResult: boolean;
  requiresReview: boolean;
  allowChangeDeadline: boolean;
  deadline?: DeadlineOffset | null;
  checklists: TemplateChecklist[];
  subtasks: TemplateSubtask[];
}

export type TemplateScope = 'personal' | 'shared';

export interface TemplateListItem {
  id: string;
  name: string;
  scope: TemplateScope;
  owner: UserRef | null;
  canEdit: boolean;
  useCount: number;
  updatedAt: string;
  title: string;
  checklistItems: number;
  subtasks: number;
  hasDeadline: boolean;
}

export interface TaskTemplate {
  id: string;
  name: string;
  scope: TemplateScope;
  owner: UserRef | null;
  canEdit: boolean;
  useCount: number;
  createdAt: string;
  updatedAt: string;
  body: TemplateBody;
  /** Everybody the body names (responsible, accomplices, auditors, subtasks' people). */
  people: UserRef[];
  group: TaskGroup | null;
}

export interface SaveTemplateInput {
  name: string;
  scope: TemplateScope;
  body: TemplateBody;
}

export type RecurrenceFrequency = 'daily' | 'weekly' | 'monthly' | 'yearly';
export type RecurrenceStatus = 'active' | 'paused' | 'finished' | 'stopped';
export type MissedPolicy = 'once' | 'skip';

/** When a recurring task is made (an RRULE-like rule in the owner's time zone). */
export interface RecurrenceSpec {
  frequency: RecurrenceFrequency;
  interval: number;
  /** `HH:mm`. */
  time: string;
  /** Weekly: ISO days 1–7 (empty: the weekday of `startsOn`). */
  weekdays: number[];
  monthDay?: number | null;
  lastDay: boolean;
  /** Monthly: the n-th weekday (`week` 1–4 or −1 = the last). */
  nth?: { week: number; weekday: number } | null;
  month?: number | null;
  /** `yyyy-MM-dd`. */
  startsOn: string;
  endsOn?: string | null;
  count?: number | null;
}

export interface RecurrenceListItem {
  id: string;
  name: string;
  owner: UserRef | null;
  canManage: boolean;
  status: RecurrenceStatus;
  rule: string;
  spec: RecurrenceSpec;
  timeZone: string;
  taskTitle: string;
  responsible: UserRef | null;
  nextSlotAt: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  slotsUsed: number;
  problemCode: string | null;
  templateId: string | null;
  sourceTaskId: string | null;
}

export interface RecurrenceOccurrence {
  id: string;
  kind: 'slot' | 'manual';
  slotAt: string | null;
  createdAt: string;
  status: 'created' | 'missed' | 'skipped' | 'failed';
  reason: string | null;
  taskId: string | null;
  taskNumber: number | null;
  lateSeconds: number;
  missedSlots: number;
  error: string | null;
  note: string | null;
  createdBy: UserRef | null;
}

export interface Recurrence {
  id: string;
  name: string;
  owner: UserRef | null;
  canManage: boolean;
  status: RecurrenceStatus;
  rule: string;
  spec: RecurrenceSpec;
  timeZone: string;
  missedPolicy: MissedPolicy;
  templateId: string | null;
  templateName: string | null;
  sourceTaskId: string | null;
  sourceTaskNumber: number | null;
  body: TemplateBody;
  people: UserRef[];
  nextSlotAt: string | null;
  lastSlotAt: string | null;
  slotsUsed: number;
  remaining: number | null;
  lastTaskId: string | null;
  lastRunAt: string | null;
  lastRunStatus: string | null;
  problemCode: string | null;
  problemMessage: string | null;
  problemAt: string | null;
  createdAt: string;
  upcoming: string[];
  occurrences: RecurrenceOccurrence[];
}

/** A recurring task as the task card shows it. */
export interface RecurrenceBrief {
  id: string;
  name: string;
  status: RecurrenceStatus;
  spec: RecurrenceSpec;
  rule: string;
  timeZone: string;
  nextSlotAt: string | null;
  owner: UserRef | null;
  canManage: boolean;
}

export interface TaskRecurrenceInfo {
  madeBy: RecurrenceBrief | null;
  slotAt: string | null;
  repeats: RecurrenceBrief[];
}

export interface CreateRecurrenceInput {
  name?: string;
  templateId?: string;
  taskId?: string;
  spec: RecurrenceSpec;
  timeZone?: string;
  missedPolicy?: MissedPolicy;
}

export interface UpdateRecurrenceInput {
  name?: string;
  spec: RecurrenceSpec;
  timeZone?: string;
  missedPolicy?: MissedPolicy;
}

export interface RecurrencePreview {
  rule: string;
  timeZone: string;
  slots: string[];
}

/* ------------------------------------------------------------------ query keys */

export const taskKeys = {
  all: ['tasks'] as const,
  lists: ['tasks', 'list'] as const,
  list: (filters: TaskFilters) => ['tasks', 'list', filters] as const,
  boards: ['tasks', 'board'] as const,
  board: (filters: TaskFilters) => ['tasks', 'board', filters] as const,
  counters: ['tasks', 'counters'] as const,
  projects: ['tasks', 'projects'] as const,
  gantt: (groupId: string) => ['tasks', 'gantt', groupId] as const,
  groupAccess: (groupId: string) => ['tasks', 'group-access', groupId] as const,
  groupAutomation: (groupId: string) => ['tasks', 'group-automation', groupId] as const,
  linked: (module: string, type: string, id: string) => ['tasks', 'linked', module, type, id] as const,
  ganttAll: ['tasks', 'gantt'] as const,
  detail: (id: string) => ['tasks', 'detail', id] as const,
  details: ['tasks', 'detail'] as const,
  comments: (id: string) => ['tasks', 'comments', id] as const,
  history: (id: string) => ['tasks', 'history', id] as const,
  time: (id: string) => ['tasks', 'time', id] as const,
  timer: ['tasks', 'timer'] as const,
  plan: ['tasks', 'plan'] as const,
  team: ['tasks', 'team'] as const,
  settings: ['tasks', 'settings'] as const,
  trash: ['tasks', 'trash'] as const,
  trashList: (query: TrashQuery) => ['tasks', 'trash', query] as const,
  savedFilters: ['tasks', 'saved-filters'] as const,
  templates: ['tasks', 'templates'] as const,
  template: (id: string) => ['tasks', 'templates', id] as const,
  recurrences: ['tasks', 'recurrences'] as const,
  recurrence: (id: string) => ['tasks', 'recurrences', id] as const,
  tags: (search: string) => ['tasks', 'tags', search] as const,
};

/** Everything that shows task rows (lists, boards, plan, Gantt, counters). */
export function invalidateTaskViews(queryClient: QueryClient): void {
  for (const key of [
    taskKeys.lists,
    taskKeys.boards,
    taskKeys.counters,
    taskKeys.plan,
    taskKeys.ganttAll,
    taskKeys.projects,
    taskKeys.team,
    taskKeys.trash,
  ]) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

/** One task changed: its card, its tabs and every view. */
export function invalidateTask(queryClient: QueryClient, id: string): void {
  void queryClient.invalidateQueries({ queryKey: taskKeys.detail(id) });
  void queryClient.invalidateQueries({ queryKey: taskKeys.history(id) });
  void queryClient.invalidateQueries({ queryKey: taskKeys.time(id) });
  invalidateTaskViews(queryClient);
}

/* ------------------------------------------------------------------ fetchers */

const base = '/tasks';
const path = (id: string, rest = '') => `${base}/${encodeURIComponent(id)}${rest}`;

export const tasksApi = {
  list: (filters: TaskFilters, signal?: AbortSignal) =>
    api.get<PagedResult<TaskListItem>>(base, { query: { ...filters }, signal }),
  board: (filters: TaskFilters, signal?: AbortSignal) =>
    api.get<TaskBoard>(`${base}/board`, { query: { ...filters }, signal }),
  counters: (signal?: AbortSignal) => api.get<TaskCounters>(`${base}/counters`, { signal }),
  projects: (signal?: AbortSignal) => api.get<TaskProject[]>(`${base}/projects`, { signal }),
  gantt: (groupId: string, signal?: AbortSignal) =>
    api.get<GanttData>(`${base}/gantt`, { query: { groupId }, signal }),
  groupAccess: (groupId: string, signal?: AbortSignal) =>
    api.get<TaskGroupAccess>(`${base}/groups/${encodeURIComponent(groupId)}/access`, { signal }),
  groupAutomation: (groupId: string, signal?: AbortSignal) =>
    api.get<TaskGroupAutomation>(`${base}/groups/${encodeURIComponent(groupId)}/automation`, { signal }),
  linked: (module: string, type: string, id: string, signal?: AbortSignal) =>
    api.get<TaskListItem[]>(`${base}/linked`, { query: { module, type, id }, signal }),
  tags: (search: string, signal?: AbortSignal) =>
    api.get<string[]>(`${base}/tags`, { query: { search }, signal }),
  get: (id: string, signal?: AbortSignal) => api.get<TaskDetail>(path(id), { signal }),
  create: (input: CreateTaskInput) => api.post<TaskDetail>(base, input),
  update: (id: string, input: UpdateTaskInput) => api.patch<TaskDetail>(path(id), input),
  remove: (id: string) => api.delete(path(id)),
  act: (id: string, action: TaskAction) => api.post<TaskDetail>(path(id, '/status'), { action }),
  follow: (id: string) => api.post<TaskDetail>(path(id, '/follow')),
  unfollow: (id: string) => api.delete(path(id, '/follow')),
  view: (id: string) => api.post(path(id, '/view')),
  boardPosition: (id: string, afterId: string | null, beforeId: string | null) =>
    api.put(path(id, '/board-position'), { afterId, beforeId }),
  addAttachments: (id: string, fileIds: string[]) =>
    api.post<TaskDetail>(path(id, '/attachments'), { fileIds }),
  removeAttachment: (id: string, fileId: string) =>
    api.delete<TaskDetail>(path(id, `/attachments/${encodeURIComponent(fileId)}`)),
  addDependency: (id: string, predecessorId: string) =>
    api.post<TaskDetail>(path(id, '/dependencies'), { predecessorId }),
  removeDependency: (id: string, predecessorId: string) =>
    api.delete<TaskDetail>(path(id, `/dependencies/${encodeURIComponent(predecessorId)}`)),
  history: (id: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<HistoryEntry>>(path(id, '/history'), { query: { cursor, limit: 30 }, signal }),

  comments: (id: string, cursor: string | null, signal?: AbortSignal) =>
    api.get<CursorPage<TaskComment>>(path(id, '/comments'), { query: { cursor, limit: 20 }, signal }),
  addComment: (id: string, body: { bodyHtml: string; attachmentIds: string[]; isResult?: boolean }) =>
    api.post<TaskComment>(path(id, '/comments'), body),
  updateComment: (id: string, commentId: string, body: { bodyHtml: string; attachmentIds: string[] }) =>
    api.put<TaskComment>(path(id, `/comments/${encodeURIComponent(commentId)}`), body),
  removeComment: (id: string, commentId: string) =>
    api.delete(path(id, `/comments/${encodeURIComponent(commentId)}`)),
  react: (id: string, commentId: string, type: ReactionType) =>
    api.post<ReactionSummary[]>(path(id, `/comments/${encodeURIComponent(commentId)}/reactions`), { type }),

  addChecklist: (id: string, title: string) => api.post<Checklist[]>(path(id, '/checklists'), { title }),
  renameChecklist: (id: string, checklistId: string, title: string) =>
    api.put<Checklist[]>(path(id, `/checklists/${encodeURIComponent(checklistId)}`), { title }),
  removeChecklist: (id: string, checklistId: string) =>
    api.delete<Checklist[]>(path(id, `/checklists/${encodeURIComponent(checklistId)}`)),
  addChecklistItem: (id: string, checklistId: string, text: string, assigneeId?: string | null) =>
    api.post<Checklist[]>(path(id, `/checklists/${encodeURIComponent(checklistId)}/items`), {
      text,
      assigneeId,
    }),
  updateChecklistItem: (
    id: string,
    itemId: string,
    body: Partial<{ text: string; isDone: boolean; assigneeId: string | null }>,
  ) => api.patch<Checklist[]>(path(id, `/checklist-items/${encodeURIComponent(itemId)}`), body),
  removeChecklistItem: (id: string, itemId: string) =>
    api.delete<Checklist[]>(path(id, `/checklist-items/${encodeURIComponent(itemId)}`)),
  moveChecklistItem: (id: string, itemId: string, checklistId: string, index: number) =>
    api.put<Checklist[]>(path(id, `/checklist-items/${encodeURIComponent(itemId)}/position`), {
      checklistId,
      index,
    }),

  time: (id: string, signal?: AbortSignal) => api.get<TaskTime>(path(id, '/time'), { signal }),
  startTimer: (id: string) => api.post<TaskTime>(path(id, '/timer/start')),
  stopTimer: (id: string) => api.post<TaskTime>(path(id, '/timer/stop')),
  addTime: (id: string, body: { startedAt: string; seconds: number; comment?: string | null }) =>
    api.post<TaskTime>(path(id, '/time'), body),
  updateTime: (
    id: string,
    entryId: string,
    body: { startedAt: string; seconds: number; comment?: string | null },
  ) => api.put<TaskTime>(path(id, `/time/${encodeURIComponent(entryId)}`), body),
  removeTime: (id: string, entryId: string) =>
    api.delete<TaskTime>(path(id, `/time/${encodeURIComponent(entryId)}`)),
  myTimer: async (signal?: AbortSignal) =>
    (await api.get<TaskTimer | undefined>(`${base}/timer`, { signal })) ?? null,

  plan: (signal?: AbortSignal) => api.get<Plan>(`${base}/plan`, { signal }),
  addStage: (title: string, color: string | null) =>
    api.post<PlanStage[]>(`${base}/plan/stages`, { title, color }),
  updateStage: (stageId: string, title: string, color: string | null) =>
    api.put<PlanStage[]>(`${base}/plan/stages/${encodeURIComponent(stageId)}`, { title, color }),
  removeStage: (stageId: string) =>
    api.delete<PlanStage[]>(`${base}/plan/stages/${encodeURIComponent(stageId)}`),
  reorderStages: (stageIds: string[]) => api.put<PlanStage[]>(`${base}/plan/stages/order`, { stageIds }),
  movePlanItem: (
    taskId: string,
    stageId: string | null,
    afterTaskId: string | null,
    beforeTaskId: string | null,
  ) => api.put(`${base}/plan/items/${encodeURIComponent(taskId)}`, { stageId, afterTaskId, beforeTaskId }),

  team: (signal?: AbortSignal) => api.get<TaskTeam>(`${base}/team`, { signal }),
  settings: (signal?: AbortSignal) => api.get<TaskSettings>(`${base}/settings`, { signal }),
  updateSettings: (input: UpdateTaskSettingsInput) => api.put<TaskSettings>(`${base}/settings`, input),

  trash: (query: TrashQuery, signal?: AbortSignal) =>
    api.get<TrashPage>(`${base}/trash`, { query: { ...query }, signal }),
  restore: (id: string) => api.post<RestoreResult>(`${base}/trash/${encodeURIComponent(id)}/restore`),
  purge: (id: string) => api.delete<PurgeResult>(`${base}/trash/${encodeURIComponent(id)}`),
  emptyTrash: () => api.delete<PurgeResult>(`${base}/trash`),
  bulk: (request: BulkRequest) => api.post<BulkResult>(`${base}/bulk`, request),

  savedFilters: (signal?: AbortSignal) => api.get<SavedFilter[]>(`${base}/filters`, { signal }),
  createSavedFilter: (input: SaveFilterInput) => api.post<SavedFilter>(`${base}/filters`, input),
  updateSavedFilter: (id: string, input: SaveFilterInput) =>
    api.put<SavedFilter>(`${base}/filters/${encodeURIComponent(id)}`, input),
  removeSavedFilter: (id: string) => api.delete(`${base}/filters/${encodeURIComponent(id)}`),

  /**
   * The list as a CSV or XLSX file (`GET /api/tasks/export`): the same query as the list, at most 10 000 tasks. Read with `fetch`
   * as bytes: a CSV keeps its byte order mark (Excel needs it for Cyrillic), which a text read would drop.
   */
  exportFile: async (filters: TaskFilters, format: 'csv' | 'xlsx'): Promise<ExportedFile> => {
    const url = buildUrl(`${base}/export`, { ...filters, page: undefined, pageSize: undefined, format });
    const request = async (token: string | null) =>
      fetch(url, {
        headers: { Accept: '*/*', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        credentials: 'same-origin',
      });
    let response = await request(await getValidAccessToken());
    if (response.status === 401) {
      const token = await refreshAccessToken();
      if (token) response = await request(token);
    }
    if (!response.ok) throw await apiErrorFromResponse(response);
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const named = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/iu.exec(disposition)?.[1];
    return {
      blob: await response.blob(),
      fileName: named ? decodeURIComponent(named) : `tasks.${format}`,
      truncated: response.headers.get('X-Orbita-Truncated') === 'true',
      rows: Number(response.headers.get('X-Orbita-Rows') ?? 0),
    };
  },

  templates: (signal?: AbortSignal) => api.get<TemplateListItem[]>(`${base}/templates`, { signal }),
  template: (id: string, signal?: AbortSignal) =>
    api.get<TaskTemplate>(`${base}/templates/${encodeURIComponent(id)}`, { signal }),
  createTemplate: (input: SaveTemplateInput) => api.post<TaskTemplate>(`${base}/templates`, input),
  createTemplateFromTask: (taskId: string, name?: string, scope?: TemplateScope) =>
    api.post<TaskTemplate>(`${base}/templates/from-task`, { taskId, name, scope }),
  updateTemplate: (id: string, input: SaveTemplateInput) =>
    api.put<TaskTemplate>(`${base}/templates/${encodeURIComponent(id)}`, input),
  removeTemplate: (id: string) => api.delete(`${base}/templates/${encodeURIComponent(id)}`),
  instantiateTemplate: (
    id: string,
    overrides: {
      title?: string;
      responsibleId?: string;
      deadline?: string | null;
      groupId?: string | null;
    } = {},
  ) => api.post<TaskDetail>(`${base}/templates/${encodeURIComponent(id)}/instantiate`, overrides),

  recurrences: (all: boolean, signal?: AbortSignal) =>
    api.get<RecurrenceListItem[]>(`${base}/recurrences`, { query: { all: all || undefined }, signal }),
  recurrence: (id: string, signal?: AbortSignal) =>
    api.get<Recurrence>(`${base}/recurrences/${encodeURIComponent(id)}`, { signal }),
  createRecurrence: (input: CreateRecurrenceInput) => api.post<Recurrence>(`${base}/recurrences`, input),
  updateRecurrence: (id: string, input: UpdateRecurrenceInput) =>
    api.put<Recurrence>(`${base}/recurrences/${encodeURIComponent(id)}`, input),
  recurrenceAction: (id: string, action: 'pause' | 'resume' | 'stop' | 'create-now') =>
    api.post<Recurrence>(`${base}/recurrences/${encodeURIComponent(id)}/${action}`),
  removeRecurrence: (id: string) => api.delete(`${base}/recurrences/${encodeURIComponent(id)}`),
  previewRecurrence: (spec: RecurrenceSpec, timeZone?: string, count?: number) =>
    api.post<RecurrencePreview>(`${base}/recurrences/preview`, { spec, timeZone, count }),
};

/* ------------------------------------------------------------------ hooks */

/** A head gets no live events for the tasks of their people: the «Руковожу» views ask again every minute. */
const MANAGED_REFRESH_MS = 60_000;

export function useTaskList(filters: TaskFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.list(filters),
    queryFn: ({ signal }) => tasksApi.list(filters, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
    refetchInterval: filters.role === 'managed' ? MANAGED_REFRESH_MS : false,
  });
}

export function useTaskBoard(filters: TaskFilters, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.board(filters),
    queryFn: ({ signal }) => tasksApi.board(filters, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
    refetchInterval: filters.role === 'managed' ? MANAGED_REFRESH_MS : false,
  });
}

export function useTaskCounters(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.counters,
    queryFn: ({ signal }) => tasksApi.counters(signal),
    staleTime: 60_000,
    refetchInterval: 5 * 60_000,
    enabled: options.enabled ?? true,
  });
}

export function useTaskProjects() {
  return useQuery({
    queryKey: taskKeys.projects,
    queryFn: ({ signal }) => tasksApi.projects(signal),
    staleTime: 60_000,
  });
}

export function useGantt(groupId: string | null) {
  return useQuery({
    queryKey: taskKeys.gantt(groupId ?? ''),
    queryFn: ({ signal }) => tasksApi.gantt(groupId ?? '', signal),
    enabled: !!groupId,
  });
}

export function useTaskGroupAccess(groupId: string | null) {
  return useQuery({
    queryKey: taskKeys.groupAccess(groupId ?? ''),
    queryFn: ({ signal }) => tasksApi.groupAccess(groupId ?? '', signal),
    enabled: !!groupId,
  });
}

/** Whether the caller sets up the robots of a project (the link «Роботы» of its tasks). */
export function useTaskGroupAutomation(groupId: string | null) {
  return useQuery({
    queryKey: taskKeys.groupAutomation(groupId ?? ''),
    queryFn: ({ signal }) => tasksApi.groupAutomation(groupId ?? '', signal),
    enabled: !!groupId,
    staleTime: 60_000,
  });
}

/** The tasks linked to a record of another module (the «Задачи» tab of a deal's card); the ones the caller sees. */
export function useLinkedTasks(module: string, type: string, id: string) {
  return useQuery({
    queryKey: taskKeys.linked(module, type, id),
    queryFn: ({ signal }) => tasksApi.linked(module, type, id, signal),
    staleTime: 15_000,
  });
}

export function useTask(id: string | null) {
  return useQuery({
    queryKey: taskKeys.detail(id ?? ''),
    queryFn: ({ signal }) => tasksApi.get(id ?? '', signal),
    enabled: !!id,
  });
}

export function useTaskTime(id: string, enabled = true) {
  return useQuery({
    queryKey: taskKeys.time(id),
    queryFn: ({ signal }) => tasksApi.time(id, signal),
    enabled,
  });
}

export function useMyTimer(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.timer,
    queryFn: ({ signal }) => tasksApi.myTimer(signal),
    staleTime: 60_000,
    enabled: options.enabled ?? true,
  });
}

export function usePlan() {
  return useQuery({ queryKey: taskKeys.plan, queryFn: ({ signal }) => tasksApi.plan(signal) });
}

export function useTagSuggestions(search: string, enabled: boolean) {
  return useQuery({
    queryKey: taskKeys.tags(search),
    queryFn: ({ signal }) => tasksApi.tags(search, signal),
    enabled,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/** Puts a fresh card into the cache and refreshes the views showing it. */
function useApplyDetail() {
  const queryClient = useQueryClient();
  return (task: TaskDetail) => {
    queryClient.setQueryData(taskKeys.detail(task.id), task);
    void queryClient.invalidateQueries({ queryKey: taskKeys.history(task.id) });
    invalidateTaskViews(queryClient);
  };
}

/** Inline edit of a task: optimistic for simple fields, server copy on success. */
export function useUpdateTask(id: string) {
  const queryClient = useQueryClient();
  const apply = useApplyDetail();
  return useMutation({
    mutationFn: (input: UpdateTaskInput) => tasksApi.update(id, input),
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: taskKeys.detail(id) });
      const previous = queryClient.getQueryData<TaskDetail>(taskKeys.detail(id));
      if (previous) {
        const optimistic: Partial<TaskDetail> = {};
        for (const key of [
          'title',
          'priority',
          'deadline',
          'startPlan',
          'endPlan',
          'tags',
          'estimateSeconds',
          'requireResult',
          'requiresReview',
          'allowChangeDeadline',
          'reminderMinutes',
        ] as const) {
          if (key in input) Object.assign(optimistic, { [key]: input[key] });
        }
        queryClient.setQueryData(taskKeys.detail(id), { ...previous, ...optimistic });
      }
      return { previous };
    },
    onError: (_error, _input, context) => {
      if (context?.previous) queryClient.setQueryData(taskKeys.detail(id), context.previous);
    },
    onSuccess: apply,
  });
}

export function useTaskAction(id: string) {
  const apply = useApplyDetail();
  return useMutation({
    mutationFn: (action: TaskAction) => tasksApi.act(id, action),
    onSuccess: apply,
  });
}

export function useCreateTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateTaskInput) => tasksApi.create(input),
    meta: { silent: true },
    onSuccess: (task) => {
      queryClient.setQueryData(taskKeys.detail(task.id), task);
      invalidateTaskViews(queryClient);
      if (task.parent) void queryClient.invalidateQueries({ queryKey: taskKeys.detail(task.parent.id) });
    },
  });
}

export function useDeleteTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.remove(id),
    onSuccess: (_result, id) => {
      queryClient.removeQueries({ queryKey: taskKeys.detail(id) });
      invalidateTaskViews(queryClient);
    },
  });
}

/** Checklist mutations return every checklist of the task: patch the card. */
export function useChecklistMutation<TArgs>(id: string, fn: (args: TArgs) => Promise<Checklist[]>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: (checklists) => {
      queryClient.setQueryData<TaskDetail>(taskKeys.detail(id), (task) =>
        task ? { ...task, checklists } : task,
      );
      void queryClient.invalidateQueries({ queryKey: taskKeys.history(id) });
      invalidateTaskViews(queryClient);
    },
  });
}

/** Detail mutations (attachments, dependencies, follow) return the card. */
export function useDetailMutation<TArgs>(fn: (args: TArgs) => Promise<TaskDetail>) {
  const apply = useApplyDetail();
  return useMutation({ mutationFn: fn, onSuccess: apply });
}

/* ------------------------------------------------------------------ manager view, templates, recurring tasks */

/** «Руковожу»: the people the user manages who have open tasks. Refreshes by itself (the head gets no live events for them). */
export function useTaskTeam(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.team,
    queryFn: ({ signal }) => tasksApi.team(signal),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useTaskSettings() {
  return useQuery({
    queryKey: taskKeys.settings,
    queryFn: ({ signal }) => tasksApi.settings(signal),
    staleTime: 60_000,
  });
}

export function useUpdateTaskSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateTaskSettingsInput) => tasksApi.updateSettings(input),
    onSuccess: (settings) => {
      queryClient.setQueryData(taskKeys.settings, settings);
      invalidateTaskViews(queryClient);
    },
  });
}

/* ------------------------------------------------------------------ trash, bulk actions, saved filters */

export function useTaskTrash(query: TrashQuery, options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.trashList(query),
    queryFn: ({ signal }) => tasksApi.trash(query, signal),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });
}

/** Brings a task back from the trash: the views and the trash itself refresh. */
export function useRestoreTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.restore(id),
    meta: { silent: true },
    onSuccess: (result) => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.detail(result.id) });
      invalidateTaskViews(queryClient);
    },
  });
}

export function usePurgeTask() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tasksApi.purge(id),
    meta: { silent: true },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: taskKeys.trash }),
  });
}

export function useEmptyTrash() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => tasksApi.emptyTrash(),
    meta: { silent: true },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: taskKeys.trash }),
  });
}

/** One action for many tasks; whatever it changed shows in every view. */
export function useBulkTasks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: BulkRequest) => tasksApi.bulk(request),
    meta: { silent: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.details });
      invalidateTaskViews(queryClient);
    },
  });
}

export function useSavedFilters(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.savedFilters,
    queryFn: ({ signal }) => tasksApi.savedFilters(signal),
    staleTime: 30_000,
    enabled: options.enabled ?? true,
  });
}

/** Saved-filter mutations refresh the menu. */
export function useSavedFilterMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    meta: { silent: true },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: taskKeys.savedFilters }),
  });
}

export function useTemplates(options: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: taskKeys.templates,
    queryFn: ({ signal }) => tasksApi.templates(signal),
    enabled: options.enabled ?? true,
    staleTime: 30_000,
  });
}

export function useTemplate(id: string | null) {
  return useQuery({
    queryKey: taskKeys.template(id ?? ''),
    queryFn: ({ signal }) => tasksApi.template(id ?? '', signal),
    enabled: !!id,
  });
}

/** Template mutations refresh the list and the template itself. */
export function useTemplateMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    meta: { silent: true },
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: taskKeys.templates }),
  });
}

/** A task made from a template (or a recurring task): the views show it. */
export function useInstantiateTemplate() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; overrides?: Parameters<typeof tasksApi.instantiateTemplate>[1] }) =>
      tasksApi.instantiateTemplate(args.id, args.overrides),
    meta: { silent: true },
    onSuccess: (task) => {
      queryClient.setQueryData(taskKeys.detail(task.id), task);
      invalidateTaskViews(queryClient);
      void queryClient.invalidateQueries({ queryKey: taskKeys.templates });
    },
  });
}

export function useRecurrences(all = false) {
  return useQuery({
    queryKey: [...taskKeys.recurrences, { all }] as const,
    queryFn: ({ signal }) => tasksApi.recurrences(all, signal),
    staleTime: 15_000,
  });
}

export function useRecurrence(id: string | null) {
  return useQuery({
    queryKey: taskKeys.recurrence(id ?? ''),
    queryFn: ({ signal }) => tasksApi.recurrence(id ?? '', signal),
    enabled: !!id,
  });
}

/** Recurring-task mutations refresh the lists, the series and the cards that show it. */
export function useRecurrenceMutation<TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    meta: { silent: true },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: taskKeys.recurrences });
      void queryClient.invalidateQueries({ queryKey: taskKeys.details });
      void queryClient.invalidateQueries({ queryKey: taskKeys.templates });
      invalidateTaskViews(queryClient);
    },
  });
}
